import {
    FEATURE_AUTO_COMMUNICATION,
    FEATURE_COMMISSION_REPORTS_PASSWORD,
    FEATURE_CTP_PLATFORM_DEFAULTS,
    FEATURE_CTP_PLATFORM_DOCUMENTS,
    FEATURE_CTP_PLATFORM_EMAILS,
    FEATURE_CTP_PLATFORM_NOTIFICATIONS,
    FEATURE_CUSTOM_SIGNATURE,
    FEATURE_CUSTOMER_PORTAL_SETTINGS,
    FEATURE_GROSS_COMMISSION_VISIBILITY,
    FEATURE_INTEGRATION_SETTINGS,
    FEATURE_MAILCHIMP,
    FEATURE_MARKETING_CRM_SETTINGS,
    FEATURE_MARKETING_INFO,
    FEATURE_MOPOLO,
    FEATURE_PERSONAL_INFO,
    FEATURE_PERSONAL_SETTINGS,
    FEATURE_PROVINCIAL_TAX_RATE,
    FEATURE_REVENUE_SETTINGS,
    FEATURE_SECURITY_SETTINGS,
    FEATURE_SYSTEM_SETTINGS,
    FEATURE_USER_SETTINGS,
    FEATURE_WEBSITE,
} from '@/constants/featureIds'
import {
    SECURITY_GROUP_ADMIN,
    SECURITY_GROUP_FEATURE_ADMIN,
    SECURITY_GROUP_TL_REVENUE_SETTING,
} from '@/constants/securityGroups'

/**
 * A single candidate landing destination for the Settings index route.
 * `features` / `groups` are the entitlements the user must hold (ALL of them) for
 * this destination to be chosen.
 */
type LandingCandidate = { path: string; features?: number[]; groups?: number[] }

/**
 * Ordered list of Settings landing destinations, highest priority first.
 *
 * This is a faithful port of the legacy React 15 `getEntryRoute()`
 * (mortgageboss_web/src/routes/main/settings/routeValidation.js): the ORDER mirrors
 * legacy `routeIds` + `subMenuMapping` descent, and each candidate's requirements
 * mirror the corresponding nav gate in `settingsNavItems.ts`, so the chosen route is
 * always one the user can both see in the sidebar and pass the route guard for.
 *
 * `broker-rates` and `preset-commission` are intentional legacy exceptions: they are
 * absent from the feature-management console, so legacy treated them as always
 * accessible within their parent group — hence they gate only on the parent group
 * feature here.
 *
 * The two security-group entries at the end are the legacy fallbacks (Admin ->
 * Website Settings, Feature-Admin -> Feature Management) reached only when the user
 * has none of the feature-gated groups above.
 */
export const SETTINGS_LANDING_CANDIDATES: LandingCandidate[] = [
    { path: '/system-settings', features: [FEATURE_SYSTEM_SETTINGS] },
    { path: '/personal-information', features: [FEATURE_PERSONAL_SETTINGS, FEATURE_PERSONAL_INFO] },
    {
        path: '/provincial-tax-rate',
        features: [FEATURE_PERSONAL_SETTINGS, FEATURE_PROVINCIAL_TAX_RATE],
    },
    {
        path: '/marketing-information',
        features: [FEATURE_MARKETING_CRM_SETTINGS, FEATURE_MARKETING_INFO],
    },
    { path: '/website', features: [FEATURE_MARKETING_CRM_SETTINGS, FEATURE_WEBSITE] },
    // legacy always-accessible exception (absent from the feature console)
    { path: '/broker-rates', features: [FEATURE_MARKETING_CRM_SETTINGS] },
    { path: '/mopolo', features: [FEATURE_MARKETING_CRM_SETTINGS, FEATURE_MOPOLO] },
    {
        path: '/auto-communication',
        features: [FEATURE_MARKETING_CRM_SETTINGS, FEATURE_AUTO_COMMUNICATION],
    },
    {
        path: '/custom-signature',
        features: [FEATURE_MARKETING_CRM_SETTINGS, FEATURE_CUSTOM_SIGNATURE],
    },
    { path: '/mailchimp', features: [FEATURE_MARKETING_CRM_SETTINGS, FEATURE_MAILCHIMP] },
    { path: '/user-settings', features: [FEATURE_USER_SETTINGS] },
    // legacy Security Settings has no submenu; modern only surfaces the PIN Code leaf
    { path: '/pin-code', features: [FEATURE_SECURITY_SETTINGS] },
    {
        path: '/commission-reports-password',
        features: [FEATURE_REVENUE_SETTINGS, FEATURE_COMMISSION_REPORTS_PASSWORD],
    },
    // Legacy always-accessible exception (absent from the feature console). The OLD Preset
    // Commission screen was retired (C260315); the revenue default now targets New Preset
    // Commission. A revenue user without the New-Preset feature resolves here but the route's
    // own FeatureGuardedRoute then shows Access Denied.
    { path: '/new-preset-commission', features: [FEATURE_REVENUE_SETTINGS] },
    {
        path: '/gross-commission',
        features: [FEATURE_REVENUE_SETTINGS, FEATURE_GROSS_COMMISSION_VISIBILITY],
        groups: [SECURITY_GROUP_TL_REVENUE_SETTING],
    },
    { path: '/customer-portal', features: [FEATURE_CUSTOMER_PORTAL_SETTINGS] },
    {
        path: '/ctp-platform-defaults',
        features: [FEATURE_CUSTOMER_PORTAL_SETTINGS, FEATURE_CTP_PLATFORM_DEFAULTS],
    },
    {
        path: '/ctp-platform-documents',
        features: [FEATURE_CUSTOMER_PORTAL_SETTINGS, FEATURE_CTP_PLATFORM_DOCUMENTS],
    },
    {
        path: '/ctp-platform-emails',
        features: [FEATURE_CUSTOMER_PORTAL_SETTINGS, FEATURE_CTP_PLATFORM_EMAILS],
    },
    {
        path: '/ctp-platform-notifications',
        features: [FEATURE_CUSTOMER_PORTAL_SETTINGS, FEATURE_CTP_PLATFORM_NOTIFICATIONS],
    },
    { path: '/integrations', features: [FEATURE_INTEGRATION_SETTINGS] },
    // legacy fallbacks (securityGroups.includes(1) -> website_settings; FEATURE_ADMIN -> feature_management)
    { path: '/admin/website-settings', groups: [SECURITY_GROUP_ADMIN] },
    { path: '/feature-management/general', groups: [SECURITY_GROUP_FEATURE_ADMIN] },
]

/**
 * Resolves the Settings index landing route for a user's entitlements, following the
 * legacy priority order. Returns `null` when the user has access to nothing — the
 * caller should render Access Denied in that case.
 */
export function resolveSettingsLandingRoute(
    features: number[],
    securityGroups: number[],
): string | null {
    const hasAll = (ids: number[] | undefined, pool: number[]): boolean =>
        !ids || ids.every((id) => pool.includes(id))

    for (const candidate of SETTINGS_LANDING_CANDIDATES) {
        if (hasAll(candidate.features, features) && hasAll(candidate.groups, securityGroups)) {
            return candidate.path
        }
    }

    return null
}
